import { Grid, Box, Button, Flex, useDisclosure } from '@chakra-ui/react'
import { FC, useMemo, useEffect, useState } from 'react'
import { AiOutlineLineChart } from 'react-icons/ai'
import { BiCreditCardAlt, BiPackage } from 'react-icons/bi'
import { MdViewList, MdTableRows } from 'react-icons/md'
import { RiSecurePaymentLine } from 'react-icons/ri'

import useMrpPurchase from '@hook/mrp/use-purchase'
import useFilters from '@hook/shared/use-filters'
import useLayout from '@hook/shared/use-layout'
import useSessions from '@hook/shared/use-sessions'

import Layout from '@component/Layout'
import InfoCard from '@component/Layout/Card/Info'
import CardSlider from '@component/Layout/CardSlider'
import LayoutEmpty from '@component/Layout/Empty'
import LayoutFilters from '@component/Layout/Filters'
import LayoutLoader from '@component/Layout/Loader'
import TableGrid from '@component/Layout/TableGrid'
import GridQuantity from '@component/Layout/TableGrid/GridQuantity'
import LayoutTools from '@component/Layout/Tools'
import ConfirmacaoCronogramaDialog from '@component/Modules/Mrp/ConfirmacaoCronogramaDialog'
import PurchaseGroupedView from '@component/Modules/Mrp/PurchaseGroupedView'

import { getIsUsuarioCompras } from '@helper/auth/getUserTokenClaims'
import { disableActionsByDefault } from '@helper/mrp/disableActionsByDefault'
import { validateAppVersion } from '@helper/util/storangeClear'
import generic from '@service/generic'
const MrpPurchase: FC = () => {
  const { menuToggle } = useLayout()
  const { getFilters } = useFilters()
  const {
    loading,
    fetching,
    headers,
    actions,
    tableInteraction,
    data,
    filename,
    params,
    query,
    dataCards,
    filters,
    setFilters,
    setFilename,
    setQuery,
    getData,
    handleLocalParams,
    pagination,
    getSupplyCommands,
    filtersList,
    handleUpdateParams,
    clearAllFilters,
    totalCount,
    justificativaForaCronograma,
    setJustificativaForaCronograma
  } = useMrpPurchase()

  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose
  } = useDisclosure()

  const isUsuarioCompras = useMemo(() => getIsUsuarioCompras(), [])
  const [isGrouped, setIsGrouped] = useState(false)

  // Suporta array [{value}] (inicial/localStorage) e objeto {value} (react-select isMulti=false)
  const getCronogramaValue = (cron: any) =>
    Array.isArray(cron) ? cron[0]?.value : cron?.value
  const cronogramaVazioOuNao =
    getCronogramaValue(params.utilizarCronograma) !== '1'

  // Pop-up de justificativa (Comprador com "Utilizar Cronograma" = Não):
  // 'change' = ao trocar a opção para Não; 'prosseguir' = ao filtrar sem justificativa (ex.: Não vindo do localStorage)
  const [justificativaTrigger, setJustificativaTrigger] = useState<
    { type: 'change'; value: any } | { type: 'prosseguir' } | null
  >(null)

  const handleFilterChange = ({
    name,
    value
  }: {
    name: string
    value: any
  }) => {
    if (name === 'utilizarCronograma') {
      const newValue = getCronogramaValue(value)
      if (newValue === '0' && getIsUsuarioCompras()) {
        setJustificativaTrigger({ type: 'change', value })
        onConfirmOpen()
        return
      }
      if (newValue === '1') setJustificativaForaCronograma('')
    }
    handleUpdateParams({ name, value } as any)
  }

  const handleProsseguir = () => {
    if (
      getIsUsuarioCompras() &&
      cronogramaVazioOuNao &&
      !justificativaForaCronograma.trim()
    ) {
      setJustificativaTrigger({ type: 'prosseguir' })
      onConfirmOpen()
      return
    }
    getData()
  }

  const handleConfirmarJustificativa = (justificativa: string) => {
    setJustificativaForaCronograma(justificativa)
    onConfirmClose()
    if (justificativaTrigger?.type === 'change') {
      handleUpdateParams({
        name: 'utilizarCronograma',
        value: justificativaTrigger.value
      } as any)
    } else {
      getData()
    }
    setJustificativaTrigger(null)
  }

  const handleCancelarJustificativa = () => {
    // Na troca para Não, cancelar mantém "Sim" (o select é controlado por params)
    onConfirmClose()
    setJustificativaTrigger(null)
  }

  const handleClearAllFilters = () => {
    setJustificativaForaCronograma('')
    clearAllFilters()
  }

  const { hasSessions } = useSessions()

  const height = useMemo(
    () =>
      menuToggle
        ? hasSessions
          ? 'calc(100vh - 30px)'
          : '100vh'
        : hasSessions
        ? 'calc(100vh - 160px)'
        : 'calc(100vh - 142px)',
    [menuToggle, hasSessions]
  )

  const tableHeight = useMemo(
    () =>
      menuToggle
        ? hasSessions
          ? 'calc(100vh - 272px)'
          : 'calc(100vh - 241px)'
        : hasSessions
        ? 'calc(100vh - 413px)'
        : 'calc(100vh - 382px)',
    [menuToggle, hasSessions]
  )

  useEffect(() => {
    generic
      .getMaintence('manutencao')
      .then(({ data: { data } }) => {
        validateAppVersion(data.version)

        if (data.valor === 'true') {
          window.location.href = '/manutencao'
        } else {
          getFilters({
            companies: true,
            worlds: true,
            centers: true,
            providersForDistribution: true,
            categoryManagers: true,
            productManagers: true,
            compactCategories: true,
            categories: true,
            providersForPurchase: true,
            abcCurves: true,
            typeOfAlert: true,
            centerTypes: true,
            utilizarCronograma: true,
            subCategories: true
          })

          getSupplyCommands()
          handleLocalParams()
        }
      })
      .catch(() => {})

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout title="MRP Compra">
      <Grid
        w="100vw"
        bg="white"
        h={height}
        templateColumns={!filters ? '100vw' : '260px calc(100vw - 260px)'}
        isDisabled={loading || fetching || totalCount === 0}
      >
        {filters && (
          <LayoutFilters
            filters={filtersList}
            values={params}
            onChange={handleFilterChange}
            loading={loading}
            canCancel={fetching}
            onFilter={handleProsseguir}
            onClearAllFilters={handleClearAllFilters}
          />
        )}
        <Box>
          <LayoutTools
            title={filename}
            filters={filters}
            actions={actions}
            query={query}
            onSearchChange={setQuery}
            onTitleChange={setFilename}
            toggleFilters={setFilters.toggle}
            isDisabled={loading || fetching || totalCount === 0}
            disableActionsByDefault={disableActionsByDefault}
          />
          {loading ? (
            <LayoutLoader
              title="Recuperando informações..."
              message="Aguarde enquanto buscamos os registros no banco de dados"
            />
          ) : totalCount === 0 ? (
            <LayoutEmpty
              title="Nada encontrado!"
              message="Filtre sua busca ao lado para obter informações"
            />
          ) : (
            <>
              <CardSlider isFilters={filters}>
                <InfoCard
                  value={
                    dataCards.skusDistintos !== 0
                      ? Number(dataCards.skusDistintos)
                      : 0
                  }
                  title="SKUs distintos"
                  tooltip="Quantidade de SKUs distintos com sugestão de compra maior que zero"
                  icon={<BiPackage color="#1459B4 " />}
                  decimals={1}
                  isLoading={fetching}
                  noShadow
                />
                <InfoCard
                  value={
                    dataCards.custoRecomendado !== 0
                      ? Number(dataCards.custoRecomendado)
                      : 0
                  }
                  title="Custo recomendado"
                  icon={<RiSecurePaymentLine color="#C22C31" />}
                  decimals={1}
                  isLoading={fetching}
                  noShadow
                />
                <InfoCard
                  value={dataCards.volume !== 0 ? Number(dataCards.volume) : 0}
                  title="Volume total de compra"
                  icon={<AiOutlineLineChart color="#308AFF" />}
                  decimals={1}
                  isLoading={fetching}
                  noShadow
                />
                <InfoCard
                  value={dataCards.custo !== 0 ? Number(dataCards.custo) : 0}
                  title="Custo total de compra"
                  icon={<BiCreditCardAlt color="#24B85F" />}
                  decimals={3}
                  isLoading={fetching}
                  noShadow
                />
              </CardSlider>

              <Flex align="center" justify="space-between" px={2} mb={1}>
                <GridQuantity
                  qtd={String(totalCount)}
                  disabled={totalCount === 0}
                />
                {isUsuarioCompras && (
                  <Button
                    size="xs"
                    variant={isGrouped ? 'solid' : 'outline'}
                    colorScheme="blue"
                    leftIcon={isGrouped ? <MdTableRows /> : <MdViewList />}
                    onClick={() => setIsGrouped(v => !v)}
                  >
                    {isGrouped ? 'Visão Plana' : 'Visão por Fornecedor'}
                  </Button>
                )}
              </Flex>
              {isGrouped ? (
                <PurchaseGroupedView
                  data={data}
                  query={query}
                  height={tableHeight}
                  headers={headers}
                />
              ) : (
                <TableGrid
                  id="purschase-table"
                  headers={headers}
                  query={query}
                  data={data}
                  loading={fetching}
                  actions={tableInteraction}
                  height={tableHeight}
                  pagination={pagination}
                  itsSortable={true}
                  entity="mrp-purchase"
                />
              )}
            </>
          )}
        </Box>
      </Grid>
      <ConfirmacaoCronogramaDialog
        isOpen={isConfirmOpen}
        justificativaInicial={justificativaForaCronograma}
        onClose={handleCancelarJustificativa}
        onConfirm={handleConfirmarJustificativa}
      />
    </Layout>
  )
}

export default MrpPurchase
